import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'src/components/design-system';
import { EVENTS } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import {
  getInitialSelectedOrganizationId,
  getRememberedOrganizationId,
  getSelectableOrganizations,
} from 'shared/store/selectors/available-organizations.selector';
import { selectOrganization } from 'shared/store/actions/available-organizations.actions';
import { LogoSymbolWhite } from 'src/images/logos';
import analyticsService from 'src/helpers/analytics.service';
import { OrganizationCard } from './organization-card.component';
import styles from './organization-selector.module.scss';

export const OrganizationSelectorPage = () => {
  const dispatch = useDispatch();
  const lang = useSelector(getLang('ORGANIZATION_SELECTOR'));
  const organizations = useSelector(getSelectableOrganizations);
  const activeOrganizationId = useSelector(state => state.organization?.id);
  const lastUsedOrganizationId = useSelector(getRememberedOrganizationId);
  const initialSelectedId = useSelector(getInitialSelectedOrganizationId);
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [loading, setLoading] = useState(false);

  // Once per mount — a list refresh while showing is not a second impression.
  useEffect(() => {
    analyticsService.track(EVENTS.ORGANIZATION.SELECTOR_SHOWN, {
      organizationsCount: organizations.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = useCallback(async () => {
    if (!selectedId) return;

    setLoading(true);

    const { error } = await dispatch(selectOrganization({ id: selectedId }));

    // On success the guard redirects away; keep loading until then.
    if (error) {
      setLoading(false);
      message.error(lang.SWITCH_FAILED);
      return;
    }

    analyticsService.track(EVENTS.ORGANIZATION.SELECTOR_CONFIRMED, {
      sourceOrganizationId: activeOrganizationId,
      targetOrganizationId: selectedId,
    });
  }, [selectedId, activeOrganizationId, dispatch, lang.SWITCH_FAILED]);

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.brandMark}>
            <img src={LogoSymbolWhite} alt="" />
          </div>
        </div>

        <h1 className={styles.title}>{lang.HEADER}</h1>
        <p className={styles.body}>{lang.BODY}</p>

        <div className={styles.fieldHeader}>
          <span className={styles.fieldLabel} id="organization-selector-label">
            {lang.FIELD_LABEL}
            <span className={styles.requiredMark} aria-hidden="true">
              *
            </span>
          </span>
          <span className={styles.requiredHint}>{lang.REQUIRED}</span>
        </div>

        <div
          className={styles.options}
          role="radiogroup"
          aria-required="true"
          aria-labelledby="organization-selector-label">
          {organizations.map(organization => (
            <OrganizationCard
              key={organization.id}
              organization={organization}
              lang={lang}
              isSelected={organization.id === selectedId}
              isLastUsed={organization.id === lastUsedOrganizationId}
              disabled={loading}
              onSelect={setSelectedId}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <Button
            className={styles.submit}
            type="primary"
            onClick={handleContinue}
            disabled={!selectedId}
            loading={loading}>
            {lang.CONTINUE}
          </Button>
          <p className={styles.footerNote}>{lang.FOOTER_WEB}</p>
        </div>
      </div>
    </div>
  );
};
